import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Field Component (must be outside component to avoid unmount/remount on every render) ─────
const FormField = ({ label, children, labelStyle }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const NewInvoicePanel = React.memo(function NewInvoicePanel({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [client, setClient] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [product, setProduct] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const PRODUCTS = [
    { value: 'Professional Liability', label: t('screens.invoicing.productProfessionalLiability') },
    { value: 'Daily Sickness', label: t('screens.invoicing.productDailySickness') },
    { value: 'Legal Protection', label: t('screens.invoicing.productLegalProtection') },
  ];

  const handleCreate = async () => {
    if (!client.trim()) {
      setError(t('screens.invoicing.newClientRequired'));
      return;
    }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError(t('screens.invoicing.newAmountRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(
        `${BASE_URL}/invoices`,
        {
          client: client.trim(),
          ...(policyNumber.trim() && { policyNumber: policyNumber.trim() }),
          ...(product && { product }),
          amount: Number(amount),
          ...(dueDate && { dueDate }),
          status: 'draft',
        },
        { withCredentials: true },
      );
      onCreated && onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('screens.invoicing.newCreateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 13px',
    background: '#fff',
    border: `1px solid ${S.borderLight}`,
    borderRadius: 10,
    fontSize: 13,
    color: S.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5 };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{ width: 440, height: '100%', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)', overflowY: 'auto', padding: 28 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{t('screens.invoicing.newPanelTitle')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        <FormField labelStyle={labelStyle} label={t('screens.invoicing.newClientLabel')}>
          <input value={client} onChange={e => setClient(e.target.value)} placeholder={t('screens.invoicing.newClientPh')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.invoicing.newPolicyLabel')}>
          <input value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} placeholder={t('screens.invoicing.newPolicyPh')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.invoicing.newProductLabel')}>
          <select value={product} onChange={e => setProduct(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value=''>{t('screens.invoicing.newProductSelect')}</option>
            {PRODUCTS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.invoicing.newAmountLabel')}>
          <input type='number' value={amount} onChange={e => setAmount(e.target.value)} placeholder={t('screens.invoicing.newAmountPh')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.invoicing.newDueDateLabel')}>
          <input type='date' value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
        </FormField>

        {error && (
          <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
            {t('screens.invoicing.newCancel')}
          </BtnSecondary>
          <BtnPrimary onClick={handleCreate} style={{ flex: 1, justifyContent: 'center' }} disabled={saving}>
            {saving ? t('screens.invoicing.newSaving') : t('screens.invoicing.newCreate')}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
});

export default NewInvoicePanel
